'use client'

import { useEffect } from 'react'

import {
  IS_DEV,
  __APP_VERSION__,
  datadogClientToken,
  environment,
} from '@constants/env'
import { useUserContext } from '@contexts/UserContext'
import { datadogLogs, initDatadog } from '@services/datadog/datadog-logs'
import { sampleRate } from '@services/datadog/sample-rate'
import { useDatadogStore } from '@state/use-datadog-store'

const messagesToFilter = [
  'minified react',
  /**
   * According to
   * https://developers.facebook.com/community/threads/320013549791141/
   * AND
   * https://stackoverflow.com/questions/72720387/cant-find-variable-autofillcallbackhandler
   * This occurs on Instagram browsers and doesn't affect user experience so should be safe to filter out
   */
  'autofillcallbackhandler',

  /**
   * These are all network errors, we shouldn't worry about them
   */
  'failed to fetch',
  'abort',
  'load failed',
  'loading chunk',

  /**
   * These most likely a combination of issues
   * - network
   * - cors
   * - ad blockers
   * - browser settings
   * I am seeing these appear for
   * - segment
   * - our backcend url
   * - waf sdk
   * - apple maps
   * - google analytics
   */
  'fetch error post',
  'fetch error get',
  'xhr error post',
  'xhr error get',
  'failed to load',

  // these are apparently issues with google analytics and other third party scripts
  'googletag is not defined',
  'window.$ is not a function',
]

const httpStatusCodesToFilter = [0, 404]

export function Datadog() {
  const { user } = useUserContext()

  const { isInitialized } = useDatadogStore()

  useEffect(() => {
    // Don't initialized if we've already done so
    if (isInitialized) {
      return
    }

    // Don't initialize on a local env
    if (IS_DEV) {
      return
    }

    // Don't initialize without a datadog token
    if (!datadogClientToken) {
      return
    }

    // Don't initialize on the server
    // Probably not needed since useEffects only run on client, but just making sure
    if (typeof window === 'undefined') {
      return
    }

    initDatadog({
      clientToken: datadogClientToken,
      site: 'datadoghq.com',
      service: 'swimply-web',
      env: environment,
      version: __APP_VERSION__,
      sessionSampleRate: sampleRate,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error'],
      telemetrySampleRate: 0,
      beforeSend: (log) => {
        const httpErrorCode = log?.http?.status_code
        if (httpErrorCode && httpStatusCodesToFilter.includes(httpErrorCode)) {
          return false
        }

        const errorMessage =
          log?.message ??
          log?.error?.message ??
          log?.error?.causes?.[0]?.message
        // If we find that the error message includes any of the messages we want to filter
        // return false and don't send to Datadog
        if (errorMessage) {
          const isBlocked = messagesToFilter.some((message) =>
            errorMessage.toLowerCase().includes(message)
          )
          return !isBlocked
        }

        return true
      },
    })
  }, [isInitialized])

  useEffect(() => {
    if (IS_DEV) return
    if (!user?.id) {
      datadogLogs.clearUser()
      return
    }

    datadogLogs.setUser({
      id: user.id,
    })
  }, [user?.id])

  return null
}
